import {
    CommunityId,
    homeCarePartnerDescriptor,
    ReferenceBusinessId,
    ReferenceContactId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { partnerSyncPollIntervalOf } from "components/partner-sync/partner-sync-display";
import { PartnerSyncIndicator } from "components/partner-sync/partner-sync-indicator";
import {
    PartnerSyncNow,
    usePartnerSyncClock,
} from "components/partner-sync/partner-sync-status";
import { trpc } from "components/rpc/trpc";
import React, { useCallback, useMemo } from "react";
import { useFeature } from "store/selectors/hooks";

export type WellSkyReferralSourceRef =
    | { kind: "contact"; id: ReferenceContactId }
    | { kind: "business"; id: ReferenceBusinessId }
    | { kind: "community"; id: CommunityId };

export type WellSkyReferralSourceStatus = inferProcedureOutput<
    AppRouter["wellSkyReferralSource"]["send"]
>;

const { partnerName } = homeCarePartnerDescriptor("wellSkyPersonalCare");

export const useWellSkyPersonalCareConnected = (): boolean => {
    const isWellSkyEnabled = useFeature("wellSkyIntegration");
    const trpcContext = trpc.useTRPC();
    const hasCredentialQuery = useQuery(
        trpcContext.wellSkyPersonalCare.hasCredential.queryOptions(undefined, {
            enabled: isWellSkyEnabled,
        }),
    );
    return isWellSkyEnabled && (hasCredentialQuery.data?.hasCredential ?? false);
};

export const useWellSkyReferralSourceStatus = (
    referralSource: WellSkyReferralSourceRef,
) => {
    const connected = useWellSkyPersonalCareConnected();
    const trpcContext = trpc.useTRPC();
    return useQuery({
        ...trpcContext.wellSkyReferralSource.status.queryOptions({ referralSource }),
        enabled: connected,
        refetchInterval: current =>
            current.state.status === "error"
                ? false
                : partnerSyncPollIntervalOf(current.state.data?.sync ?? null, Date.now()),
    });
};

export const useRememberWellSkyReferralSourceStatus = (
    referralSource: WellSkyReferralSourceRef,
) => {
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    return useCallback(
        (status: WellSkyReferralSourceStatus) => {
            queryClient.setQueryData(
                trpcContext.wellSkyReferralSource.status.queryKey({ referralSource }),
                status,
            );
        },
        [queryClient, trpcContext, referralSource],
    );
};

export const useInvalidateWellSkyReferralSourceStatus = (): (() => void) => {
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    return useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: trpcContext.wellSkyReferralSource.status.queryKey(),
        });
    }, [queryClient, trpcContext]);
};

export const useWellSkyReferralSourceSyncNow = (
    referralSource: WellSkyReferralSourceRef,
): PartnerSyncNow => {
    const trpcContext = trpc.useTRPC();
    const rememberStatus = useRememberWellSkyReferralSourceStatus(referralSource);
    const { mutate, isPending, error } = useMutation({
        ...trpcContext.wellSkyReferralSource.syncNow.mutationOptions(),
        onSuccess: rememberStatus,
    });
    return useMemo(
        () => ({
            run: () => mutate({ referralSource }),
            isPending,
            error: error ? error.message : null,
        }),
        [mutate, isPending, error, referralSource],
    );
};

interface WellSkyReferralSourceLinkProps {
    referralSource: WellSkyReferralSourceRef;
    className: string;
}

export const WellSkyReferralSourceLink: React.FC<WellSkyReferralSourceLinkProps> = ({
    referralSource,
    className,
}) => {
    const statusQuery = useWellSkyReferralSourceStatus(referralSource);
    const syncNow = useWellSkyReferralSourceSyncNow(referralSource);
    const now = usePartnerSyncClock(statusQuery.dataUpdatedAt);
    const status = statusQuery.data ?? null;
    if (!status) {
        return null;
    }
    return (
        <div className={className}>
            <PartnerSyncIndicator
                partnerName={partnerName}
                url={status.url ?? null}
                status={status.sync}
                syncNow={syncNow}
                now={now}
            />
        </div>
    );
};
